"use client";

// One module-level store for read-aloud state.
//
// `window.speechSynthesis` is a single global queue: there is exactly one thing
// speaking at a time, browser-wide. Giving every MessageBubble its own copy of
// that state is what produced the interruption bugs in PR #193 — a bubble could
// not tell its own playback from someone else's. Modelling it as one store
// removes the problem instead of patching it, and lets the composer's auto-speak
// toggle observe the same state the bubbles do without provider plumbing.

const VOICE_KEY = "ctrl-a-speech-voice-uri";
const AUTO_KEY = "ctrl-a-speech-auto";

export interface SpeechState {
  voices: SpeechSynthesisVoice[];
  selectedVoiceURI: string | null;
  speaking: boolean;
  /**
   * WHICH message is being spoken, or null for playback with no owner.
   *
   * `speaking` alone is not enough: every MessageBubble reads this state, so a
   * bare boolean makes all of them render "Stop" at once and makes clicking
   * Listen on a second message merely cancel the first.
   */
  speakingId: string | null;
  autoSpeak: boolean;
  /**
   * Whether speechSynthesis exists. Lives in state rather than being computed
   * during render so that the server snapshot and the hydration render agree —
   * computing it inline returns false on the server and true in the browser,
   * which is a hydration mismatch on any always-rendered surface (the composer).
   */
  supported: boolean;
}

type Listener = () => void;

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private window / storage disabled — the setting just won't persist.
  }
}

export const supportsSpeech = (): boolean =>
  typeof window !== "undefined" && "speechSynthesis" in window;

// A stable object identity per state version: useSyncExternalStore compares
// snapshots by reference, so this must only be replaced when something changes.
let state: SpeechState = {
  voices: [],
  selectedVoiceURI: null,
  speaking: false,
  speakingId: null,
  autoSpeak: false,
  supported: false,
};

// A distinct frozen object, NOT an alias of the initial `state`. This module is
// evaluated on the server too ("use client" does not prevent SSR of a client
// component), so the server snapshot must be immune to anything that mutates
// module state. Nothing does today — init() is gated on a `window` check and
// subscribe() never runs during SSR — but freezing enforces the invariant
// rather than leaving it to convention.
const SERVER_SNAPSHOT: SpeechState = Object.freeze({
  voices: [],
  selectedVoiceURI: null,
  speaking: false,
  speakingId: null,
  autoSpeak: false,
  supported: false,
});

const listeners = new Set<Listener>();
let initialized = false;

function emit(next: Partial<SpeechState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  init();
  return () => {
    listeners.delete(listener);
  };
}

export const getSnapshot = (): SpeechState => state;
export const getServerSnapshot = (): SpeechState => SERVER_SNAPSHOT;

function init(): void {
  if (initialized || !supportsSpeech()) return;
  initialized = true;

  const readVoices = () => {
    const list = window.speechSynthesis.getVoices();
    if (list.length > 0) emit({ voices: list });
  };

  readVoices();
  // Voices load asynchronously in Chrome — the first getVoices() is often empty.
  window.speechSynthesis.addEventListener("voiceschanged", readVoices);

  emit({
    supported: true,
    selectedVoiceURI: readStored(VOICE_KEY),
    autoSpeak: readStored(AUTO_KEY) === "1",
  });
}

export function selectVoice(uri: string): void {
  writeStored(VOICE_KEY, uri);
  emit({ selectedVoiceURI: uri });
}

export function setAutoSpeak(on: boolean): void {
  writeStored(AUTO_KEY, on ? "1" : "0");
  // Turning it off mid-sentence should go quiet immediately, not finish the reply.
  if (!on) stop();
  emit({ autoSpeak: on });
}

// Playback is tracked by session id so that handlers belonging to cancelled
// playback cannot clobber the state of whatever is playing now.
let session = 0;

export function stop(): void {
  if (!supportsSpeech()) return;
  session += 1;
  window.speechSynthesis.cancel();
  emit({ speaking: false, speakingId: null });
}

export function speak(chunks: string[], id: string | null = null): void {
  if (!supportsSpeech() || chunks.length === 0) return;

  const mine = ++session;
  window.speechSynthesis.cancel();

  // Same resolution the hook shows in the UI, so what you see selected is what
  // you hear — a divergence here would be invisible and maddening.
  const voice =
    state.voices.find((v) => v.voiceURI === state.selectedVoiceURI) ??
    pickDefaultVoice(
      state.voices,
      typeof navigator !== "undefined" ? navigator.language : "en",
    );

  const finish = () => {
    if (session !== mine) return;
    emit({ speaking: false, speakingId: null });
  };

  chunks.forEach((chunk, i) => {
    const utterance = new SpeechSynthesisUtterance(chunk);
    if (voice) utterance.voice = voice;
    // Every chunk carries onerror: if playback is interrupted mid-queue the
    // later chunks are dropped without firing, so handlers on the last one
    // alone would leave `speaking` stuck true forever.
    utterance.onerror = finish;
    if (i === chunks.length - 1) utterance.onend = finish;
    window.speechSynthesis.speak(utterance);
  });

  emit({ speaking: true, speakingId: id });
}

// ─── auto-speak target selection (pure) ──────────────────────────────────────

export interface AutoSpeakCandidate {
  id: string;
  role: string;
  content?: string | null;
  isStreaming?: boolean;
}

/**
 * The newest finished assistant reply, if it is not the one already handled.
 *
 * Returns null when there is nothing new to say. Priming (recording the newest
 * message at mount so history is never re-read) is the caller's job: it passes
 * that id in as `lastHandledId`.
 */
export function pickAutoSpeakTarget(
  messages: readonly AutoSpeakCandidate[],
  lastHandledId: string | null,
): AutoSpeakCandidate | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg.role !== "assistant") continue;
    // Streaming replies are skipped entirely rather than spoken partially and
    // then re-spoken in full when they complete.
    if (msg.isStreaming) return null;
    if (!msg.content || !msg.content.trim()) return null;
    return msg.id === lastHandledId ? null : msg;
  }
  return null;
}

/**
 * True when a conversation-id change represents the SAME thread being assigned
 * an id, rather than a switch to a different conversation.
 *
 * A brand-new conversation gets its id from the first response, so the id and
 * the reply arrive in the same commit and the message set merely grew. A real
 * switch replaces the set. Treating the two alike silently swallows the first
 * reply of every new conversation.
 */
export function isSameThreadContinuation(
  prevIds: ReadonlySet<string>,
  currentIds: ReadonlySet<string>,
): boolean {
  if (prevIds.size === 0) return false;
  for (const id of prevIds) if (!currentIds.has(id)) return false;
  return true;
}

// ─── voice quality ───────────────────────────────────────────────────────────
//
// The Web Speech API exposes no quality field, so every voice looks alike in a
// list — the robotic compact ones sit next to the good ones with nothing to tell
// them apart. These heuristics read the only signals there are: the vendor's own
// naming, and whether the voice is on-device.
//
// On iOS the premium voices a user downloads (Settings > Accessibility > Spoken
// Content > Voices) are named "… (Enhanced)" or "… (Premium)". Apple does not
// expose Siri's own voice to web content at all; these are the closest thing.

export interface VoiceQuality {
  /** Higher is better. Used for sorting and for picking a sensible default. */
  rank: number;
  /** Short badge for the picker, or null for an ordinary voice. */
  label: string | null;
}

const PREMIUM = /\b(premium|enhanced|neural|natural)\b/i;
const KNOWN_GOOD_VENDOR = /\b(google|microsoft)\b/i;
const NOVELTY = /\b(albert|bad news|bahh|bells|boing|bubbles|cellos|good news|jester|organ|superstar|trinoids|whisper|wobble|zarvox)\b/i;

export function voiceQuality(voice: { name: string; localService?: boolean }): VoiceQuality {
  const name = voice.name ?? "";
  // Novelty voices are unusable for reading a reply; sink them regardless of
  // anything else, or "Bad News" can win a language group alphabetically.
  if (NOVELTY.test(name)) return { rank: -1, label: null };

  const premium = PREMIUM.exec(name);
  if (premium) {
    // Normalise "Enhanced"/"Premium"/"Neural" casing for the badge.
    const word = premium[0].toLowerCase();
    return { rank: 3, label: word.charAt(0).toUpperCase() + word.slice(1) };
  }
  if (KNOWN_GOOD_VENDOR.test(name)) return { rank: 2, label: null };
  // A network voice is usually the vendor's better engine; on-device compact
  // voices are the baseline.
  return { rank: voice.localService === false ? 1 : 0, label: null };
}

export interface RankableVoice {
  name: string;
  lang: string;
  localService?: boolean;
  default?: boolean;
}

/** Best first, then by name. Stable and pure, so the picker and the default
 *  selection cannot disagree about what "best" means. */
export function sortVoicesByQuality<T extends RankableVoice>(voices: readonly T[]): T[] {
  return [...voices].sort((a, b) => {
    const diff = voiceQuality(b).rank - voiceQuality(a).rank;
    return diff !== 0 ? diff : a.name.localeCompare(b.name);
  });
}

/**
 * The voice to use when the user has not chosen one.
 *
 * Browsers hand back an arbitrary first voice — frequently a compact one, and on
 * some systems not even in the user's language. Preferring the best-ranked voice
 * in the preferred language is the single biggest perceived-quality difference
 * available without a server.
 */
export function pickDefaultVoice<T extends RankableVoice>(
  voices: readonly T[],
  preferredLang: string,
): T | null {
  if (voices.length === 0) return null;
  const base = (preferredLang || "en").slice(0, 2).toLowerCase();

  const sameLang = voices.filter((v) => (v.lang ?? "").slice(0, 2).toLowerCase() === base);
  const pool = sameLang.length > 0 ? sameLang : voices;

  const best = sortVoicesByQuality(pool)[0];
  // Every candidate being novelty-tier means there is nothing good to prefer —
  // fall back to whatever the platform marked default rather than picking
  // "Zarvox" on purpose.
  if (voiceQuality(best).rank < 0) return pool.find((v) => v.default) ?? pool[0];
  return best;
}

/** Test-only: reset the singleton between cases. */
export function _resetForTests(): void {
  state = {
    voices: [],
    selectedVoiceURI: null,
    speaking: false,
    speakingId: null,
    autoSpeak: false,
    supported: false,
  };
  listeners.clear();
  initialized = false;
  session = 0;
}
